import TileWrapper from '../TileWrapper'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useOverviewRuntime, useTile } from '../../context'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { Link } from 'react-router-dom'
import { Calendar, Users } from 'lucide-react'
import dayjs from 'dayjs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { compareTeamsByDefaultAndName } from 'app/frontend/base/helpers/teamOrdering'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'

type TeamSummary = NonNullable<wowaudit.OverviewData['teamSummaries']>[number]

const TeamSummaryCard = ({ summary }: { summary: TeamSummary }) => {
  const { tile } = useTile()
  const isSidebar = tile.position === 'sidebar'
  const showRoles = !isSidebar
  const dayLabels = (summary.eventDays || []).map((day) =>
    dayjs().day(Number(day)).format('ddd'),
  )
  const eventDaysLabel = dayLabels.join(', ') || '-'

  return (
    <Card className='py-2 px-3'>
      <Link
        to={summary.path}
        className={`min-w-0 grid ${
          showRoles
            ? 'grid-cols-3 md:grid-cols-4'
            : 'grid-cols-[minmax(0,1fr)_3rem_3rem]'
        } items-center gap-4 text-sm whitespace-nowrap overflow-hidden`}
      >
        <h5 className='m-0 truncate flex items-center gap-1.5'>
          {summary.default ? <DefaultTeamStar /> : null}
          <span className='truncate'>{summary.name}</span>
        </h5>
        {showRoles ? (
          <span className='hidden md:grid grid-cols-3 gap-1'>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='tank' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.tank}
              </span>
            </span>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='heal' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.heal}
              </span>
            </span>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='damage' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.dps}
              </span>
            </span>
          </span>
        ) : null}
        <span className='flex items-center gap-1.5'>
          <Users className='h-4 w-4' />
          <span>{summary.characterAmount}</span>
        </span>
        <span className='flex items-center gap-1.5 truncate'>
          {isSidebar ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='flex items-center gap-1.5'>
                  <Calendar className='h-4 w-4 shrink-0' />
                  <span className='tabular-nums'>{dayLabels.length}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Weekly events are scheduled on: {eventDaysLabel}
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Calendar className='h-4 w-4 shrink-0' />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Weekly events are scheduled on these days
                </TooltipContent>
              </Tooltip>
              <span className='truncate'>{eventDaysLabel}</span>
            </>
          )}
        </span>
      </Link>
    </Card>
  )
}

const TeamSummaryTile = () => {
  const { guild } = useGuild()
  const { overviewData } = useOverviewRuntime()
  const teamSummaries = [...(overviewData?.teamSummaries || [])]
    .filter((summary) => summary.visibleInGuild)
    .sort(compareTeamsByDefaultAndName)

  if (teamSummaries.length === 0) {
    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No teams found</p>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper>
      <ul className='space-y-2'>
        {teamSummaries.map((summary) => {
          const teamReference = guild?.teamReferences.find(
            (reference) => reference.id === summary.id,
          )

          return (
            <li key={summary.id}>
              {teamReference ? (
                <ProtectedContent
                  section='base'
                  requirement='member'
                  teamReference={teamReference}
                  alwaysRenderContent={true}
                >
                  <TeamSummaryCard summary={summary} />
                </ProtectedContent>
              ) : (
                <TeamSummaryCard summary={summary} />
              )}
            </li>
          )
        })}
      </ul>
    </TileWrapper>
  )
}

export default TeamSummaryTile
